import { filterGamesByTerm } from '@optools/utils/filter';
import { ReactNode } from '@tanstack/react-router';
import { useState } from 'react';

import { ScrollPage } from './layout/layout';
import { PageLoader } from './loading/page-loader';
import { DebouncedSearch } from './search/debounced-search';
import { cn } from './ui/utils';
import { GameCardGame } from './game-card';
import { NoGamesToday } from './no-games-today';

export interface GameScheduleProps<TGame> {
  children: ReactNode;
  className?: string;
  ['data-testid']?: string;
  loading: boolean;
  render: (game: TGame) => ReactNode;
  schedule: TGame[] | undefined;
  scheduleClassName?: string;
}

export function GameSchedule<TGame extends GameCardGame>({
  children,
  className,
  ['data-testid']: dataTestId,
  loading,
  render,
  schedule,
  scheduleClassName,
}: GameScheduleProps<TGame>) {
  const [term, setTerm] = useState('');

  const content = (() => {
    if (!schedule?.length && loading) return <PageLoader />;

    if (!schedule?.length) {
      return <NoGamesToday />;
    }

    const filteredGames = filterGamesByTerm(schedule, term);

    return (
      <ScrollPage data-testid={dataTestId}>
        <div className={cn('grid max-w-max grid-cols-[auto_1fr_auto] gap-4', scheduleClassName)}>
          {filteredGames.map((game) => render(game))}
        </div>
      </ScrollPage>
    );
  })();

  return (
    <div className={cn('grid h-full grid-rows-[auto_1fr] gap-4', className)}>
      <div className="flex gap-2">
        {children}
        <DebouncedSearch
          aria-label="Search games"
          className="w-sm"
          defaultValue={term}
          onChange={setTerm}
        />
      </div>
      {content}
    </div>
  );
}
